<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import EstimatedTime from '@/components/ui/EstimatedTime.vue';
import Tag from '@/components/ui/InfoTag.vue';
import RouteBadge from '@/components/ui/RouteBadge.vue';
import { AppRoute } from '@/libs/routing';
import { TagValues } from '@/libs/tags-helper';
import type { Route } from '@/@types/gtfs-ressource';
import type { RowType, StopTimeType } from './types';

const props = defineProps<{
  alertCount: number;
  loading: boolean;
  /** Absent when the stop_times reference a route missing from the GTFS payload. */
  route?: Route;
  row: RowType;
}>();

const router = useRouter();

const nextStopTime = computed<StopTimeType | undefined>(() => props.row.stopTimes[0]);

const time = computed<string | undefined>(() =>
  nextStopTime.value ? displayTime(nextStopTime.value) : undefined,
);

// The departure after the one above, in the same direction. Absent on the last
// run of the day: the card then shows a single time, as the mockups do.
const secondaryTime = computed<string | undefined>(() => {
  const following = props.row.stopTimes[1];
  return following ? displayTime(following) : undefined;
});

const timeType = computed<'realtime' | 'theoretical' | 'finished'>(() => {
  const stopTime = nextStopTime.value;
  if (!stopTime) return 'finished';
  if (stopTime.realTime && !stopTime.tripUpdateFormated?.canceledTrip) return 'realtime';
  return 'theoretical';
});

// The row's tags are built from the stop_time that created the row, which is not
// necessarily the one displayed here, so the cancellation of the next departure is
// read off that departure directly rather than left to a CANCELLED tag.
const isCanceled = computed<boolean>(() => !!nextStopTime.value?.tripUpdateFormated?.canceledTrip);

// Same for the following departure: shown as a plain grey time, it would otherwise
// read as a run the passenger can still count on.
const isSecondaryCanceled = computed<boolean>(
  () => !!props.row.stopTimes[1]?.tripUpdateFormated?.canceledTrip,
);

// While the GTFS is still loading, an empty direction is not "Terminé" yet.
const isLoadingNextDeparture = computed<boolean>(() => props.loading && !nextStopTime.value);

function displayTime(stopTime: StopTimeType): string {
  const update = stopTime.tripUpdateFormated;
  if (update?.delayedTrip && !update.canceledTrip) return update.delayedTrip.contentTime;
  return stopTime.time;
}

function goToTrip() {
  const stopTime = nextStopTime.value;
  if (!stopTime) return;

  router.push({ name: AppRoute.TRIP_DETAILED, params: { tripId: stopTime.tripId } });
}
</script>

<template>
  <div class="departure-card" @click="goToTrip">
    <div class="departure-card__info">
      <div class="departure-card__identity">
        <RouteBadge v-if="route" :route="route" size="small" />
        <div class="departure-card__direction">
          <span class="departure-card__arrow"><BaseIcon name="arrow-right" /></span>
          <span class="departure-card__headsign">{{ row.tripHeadsign }}</span>
        </div>
      </div>

      <span v-if="isLoadingNextDeparture" class="departure-card__loading">{{ $t('common.loading') }}</span>
      <EstimatedTime
        v-else
        :class="{
          'departure-card__time--canceled': isCanceled,
          'departure-card__time--secondary-canceled': isSecondaryCanceled,
        }"
        :secondary-time="secondaryTime"
        :time="time"
        :type="timeType"
      />
    </div>

    <div v-if="row.tags.length > 0 || alertCount > 0" class="departure-card__tags">
      <router-link
        v-if="alertCount > 0"
        :to="{ name: AppRoute.ROUTE_ALERTS, params: { routeId: row.routeId } }"
        @click.stop
      >
        <Tag :tag-type="TagValues.TRAFFIC_INFO_COUNT" :tag-data="alertCount" />
      </router-link>
      <Tag v-for="tag in row.tags" :key="tag.tagType" :tag-type="tag.tagType" :tag-data="tag.tagData" />
    </div>
  </div>
</template>

<style lang="scss">
.departure-card {
  background-color: $color-white;
  border-radius: $radius-3xl;
  border: 1px solid $color-border-medium;
  display: flex;
  flex-direction: column;
  gap: $spacing-2;
  padding: $spacing-4;

  &:active {
    background-color: $color-neutral-lower;
  }

  &__info {
    display: flex;
    gap: $spacing-3;
  }

  &__identity {
    align-items: flex-start;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $spacing-2;
    justify-content: center;
    min-width: 0;
  }

  &__direction {
    align-items: center;
    align-self: stretch;
    display: flex;
    gap: $spacing-1;
    min-width: 0;
  }

  &__arrow {
    align-items: center;
    background-color: $color-neutral-low;
    border-radius: $radius-tag;
    color: $color-text-neutral;
    display: flex;
    flex-shrink: 0;
    font-size: 16px;
    height: 24px;
    justify-content: center;
    width: 24px;
  }

  // A long trip_headsign is clipped rather than pushing the departure time out
  &__headsign {
    color: $color-neutral-highest;
    flex: 1;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__loading {
    color: $color-neutral-higher;
    font: $text-small;
    letter-spacing: $text-small-spacing;
    white-space: nowrap;
  }

  // A cancelled departure keeps its scheduled time readable, struck through
  &__time--canceled .estimated-time__text {
    color: $color-text-neutral;
    text-decoration: line-through;
  }

  &__time--secondary-canceled .estimated-time__secondary {
    text-decoration: line-through;
  }

  // Tags overflow horizontally instead of wrapping: the card keeps one line
  &__tags {
    display: flex;
    gap: $spacing-2;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    // A tag wrapped in a link would keep its natural height while its siblings are
    // stretched to the tallest tag of the row
    > a {
      display: flex;
    }
  }
}
</style>
